import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { Panel, Field, Alert, Loading, Empty, Tag } from '../components/ui'

interface AppUser {
  id: number
  email: string
  name: string | null
  role: string
  active: number
  last_login: string | null
  created_at: string
}

export default function Users() {
  const [users, setUsers] = useState<AppUser[] | null>(null)
  const [me, setMe] = useState('')
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [form, setForm] = useState({ email: '', name: '', role: 'user' })
  const [saving, setSaving] = useState(false)

  const load = () => {
    setUsers(null)
    api
      .get<{ users: AppUser[]; me: string }>('/users')
      .then((d) => { setUsers(d.users); setMe(d.me) })
      .catch((e) => setError(e.message))
  }

  useEffect(load, [])

  const add = async () => {
    setError(''); setMsg(''); setSaving(true)
    try {
      const res = await api.post<{ user: AppUser; reactivated?: boolean }>('/users', form)
      setMsg(
        res.reactivated
          ? `${res.user.email} 原本已存在，已重新啟用`
          : `已授權 ${res.user.email}`
      )
      setForm({ email: '', name: '', role: 'user' })
      load()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const patch = async (u: AppUser, body: { name?: string; role?: string; active?: boolean }) => {
    setError(''); setMsg('')
    try {
      await api.put(`/users/${u.id}`, body)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const disable = async (u: AppUser) => {
    if (!confirm(`確定停用 ${u.email}？\n\n對方下次操作時就會被登出，帳號紀錄會保留。`)) return
    setError(''); setMsg('')
    try {
      await api.del(`/users/${u.id}`)
      setMsg(`已停用 ${u.email}`)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  return (
    <>
      <div className="page-head">
        <h2>使用者權限</h2>
        <span className="hint">只有名單上且啟用中的 Google 帳號能登入系統</span>
      </div>

      <Alert kind="error">{error}</Alert>
      {msg && <div className="alert ok">{msg}</div>}

      <Panel title="授權新帳號">
        <div className="grid c4">
          <Field label="Google 帳號 Email">
            <input
              value={form.email}
              placeholder="someone@example.com"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && add()}
            />
          </Field>
          <Field label="顯示名稱（可留空）">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="角色">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="user">一般使用者</option>
              <option value="admin">管理員（可管理權限）</option>
            </select>
          </Field>
          <Field label="&nbsp;">
            <button className="primary" onClick={add} disabled={saving} style={{ width: '100%' }}>
              {saving ? '處理中…' : '授權'}
            </button>
          </Field>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: '12px 0 0' }}>
          必須填對方 Google 帳號的 Email，且要與他登入時使用的帳號完全一致。
          授權後對方直接到系統網址用 Google 登入即可，不需要另外設定密碼。
        </p>
      </Panel>

      <Panel title="已授權名單">
        {!users ? (
          <Loading />
        ) : users.length === 0 ? (
          <Empty />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>顯示名稱</th>
                  <th style={{ width: 150 }}>角色</th>
                  <th>狀態</th>
                  <th>最後登入</th>
                  <th>加入日期</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isMe = u.email.toLowerCase() === me.toLowerCase()
                  return (
                    <tr key={u.id} style={u.active ? undefined : { color: 'var(--ink-soft)' }}>
                      <td>
                        {u.email}
                        {isMe && <span className="tag info" style={{ marginLeft: 6 }}>你自己</span>}
                      </td>
                      <td>{u.name}</td>
                      <td>
                        <select
                          value={u.role}
                          disabled={isMe}
                          title={isMe ? '不能調整自己的角色' : ''}
                          onChange={(e) => patch(u, { role: e.target.value })}
                        >
                          <option value="user">一般使用者</option>
                          <option value="admin">管理員</option>
                        </select>
                      </td>
                      <td>
                        {u.active ? <Tag value="啟用中" /> : <Tag value="已停用" />}
                      </td>
                      <td>{u.last_login ?? '—'}</td>
                      <td>{u.created_at?.slice(0, 10)}</td>
                      <td>
                        <div className="btn-row">
                          {u.active ? (
                            <button className="danger" disabled={isMe} onClick={() => disable(u)}>
                              停用
                            </button>
                          ) : (
                            <button onClick={() => patch(u, { active: true })}>重新啟用</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="alert warn" style={{ marginTop: 14, marginBottom: 0 }}>
          <b>停用會立即生效。</b>
          系統每次請求都會重新確認帳號狀態，所以對方就算還開著頁面，下一個動作就會被登出。
          <br />
          為了避免把所有人鎖在門外，你不能停用自己，也不能移除系統最後一位管理員。
        </div>
      </Panel>
    </>
  )
}
